// src/pages/AddWine.jsx
import React, { useEffect, useRef, useState, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  collection,
  addDoc,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  deleteDoc,
} from "firebase/firestore";
import { db, storage } from "../services/firebase";
import {
  ref as storageRef,
  uploadBytesResumable,
  getDownloadURL,
} from "firebase/storage";
import "../styles.css";

export default function AddWine() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const nameInputRef = useRef(null); // 👈 ref pour le champ Nom

  const [form, setForm] = useState({
    name: "",
    region: "",
    year: "",
    price: "",
    cost: "",
    barcode: "",
    description: "",
    imageUrl: "",
    category: "",
    type: "",
    forSale: true,
    trackStock: true,
    stock: 0,
  });

  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [winesList, setWinesList] = useState([]);
  const [loadingList, setLoadingList] = useState(true);

  const [categories, setCategories] = useState([]);
  const [loadingCats, setLoadingCats] = useState(true);

  // édition
  const [editingId, setEditingId] = useState(null);
  const [loadingWine, setLoadingWine] = useState(false);

  // sélection multiple
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [deletingBulk, setDeletingBulk] = useState(false);

  const allIds = useMemo(() => winesList.map((w) => w.id), [winesList]);
  const allSelected = useMemo(
    () => allIds.length > 0 && allIds.every((id) => selectedIds.has(id)),
    [allIds, selectedIds]
  );
  const selectedCount = selectedIds.size;

  // --- preview image (upload local OU URL direct)
  useEffect(() => {
    if (file) {
      const url = URL.createObjectURL(file);
      setPreview(url);
      return () => URL.revokeObjectURL(url);
    }
    setPreview(form.imageUrl || "");
  }, [file, form.imageUrl]);

  useEffect(() => {
    fetchWines();
    fetchCategories();
    // eslint-disable-next-line
  }, []);

  // 👉 Focus auto sur le champ "Nom du vin" quand on passe en mode édition
  useEffect(() => {
    if (editingId && nameInputRef.current) {
      nameInputRef.current.focus();
      // Optionnel : scroll vers le champ sur mobile
      nameInputRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [editingId]);

  // --- Firestore: catégories
  const fetchCategories = async () => {
    setLoadingCats(true);
    try {
      const snap = await getDocs(collection(db, "categoriesWine"));
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setCategories(list);
      if (!form.category && list.length) {
        setForm((s) => ({
          ...s,
          category: list[0].name,
          type: (list[0].types || [])[0] || "",
        }));
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingCats(false);
    }
  };

  // --- Firestore: vins (inventaire)
  const fetchWines = async () => {
    setLoadingList(true);
    try {
      const snap = await getDocs(collection(db, "winesek"));
      setWinesList(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingList(false);
    }
  };

  // --- handlers formulaire
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((s) => ({
      ...s,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleCategoryChange = (e) => {
    const catName = e.target.value;
    const cat = categories.find((c) => c.name === catName);
    setForm((s) => ({
      ...s,
      category: catName,
      type: cat?.types?.[0] || "",
    }));
  };

  const handleFile = (e) => {
    const f = e.target.files && e.target.files[0];
    setFile(f || null);
    if (!f && fileInputRef.current) fileInputRef.current.value = "";
  };

  // --- upload image vers Firebase Storage (ajout ET édition)
  const uploadImage = (f) =>
    new Promise((resolve, reject) => {
      if (!f) return resolve(null);

      const safeName = f.name.replace(/\s+/g, "_");
      const name = `${Date.now()}_${safeName}`;
      const sRef = storageRef(storage, `wines/${name}`);
      const task = uploadBytesResumable(sRef, f);

      setUploading(true);
      setUploadProgress(0);

      task.on(
        "state_changed",
        (snap) => {
          const pct = Math.round(
            (snap.bytesTransferred / snap.totalBytes) * 100
          );
          setUploadProgress(pct);
        },
        (err) => {
          console.error("Erreur Firebase Storage (upload):", err);
          setUploading(false);
          setError(
            "Erreur lors de l'upload de l'image (bloquée par le navigateur / antivirus ?)."
          );
          reject(err);
        },
        async () => {
          try {
            const url = await getDownloadURL(task.snapshot.ref);
            setUploading(false);
            setUploadProgress(100);
            resolve(url);
          } catch (err) {
            console.error("Erreur Firebase Storage (getDownloadURL):", err);
            setUploading(false);
            setError("Impossible de récupérer l'URL de l'image.");
            reject(err);
          }
        }
      );
    });

  // --- validation simple
  const validate = () => {
    if (!form.name.trim()) return "Le nom du vin est requis.";
    if (!form.region.trim()) return "La région est requise.";
    if (form.year && !/^\d{4}$/.test(String(form.year)))
      return "Année invalide.";
    if (form.price && isNaN(Number(form.price)))
      return "Prix invalide (FCFA).";
    if (form.cost && isNaN(Number(form.cost)))
      return "Coût invalide (FCFA).";
    if (
      form.stock !== "" &&
      (!Number.isFinite(Number(form.stock)) || Number(form.stock) < 0)
    )
      return "Stock invalide.";
    // Image obligatoire : au moins une URL ou un fichier
    if (!form.imageUrl && !file) return "L'image du produit est requise.";
    return "";
  };

  // --- Submit (ajout / édition)
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    const v = validate();
    if (v) return setError(v);

    setLoading(true);
    try {
      let imageUrl = form.imageUrl || null;

      // Si un fichier est choisi (ajout OU édition), on essaie de l’uploader
      if (file) {
        try {
          imageUrl = await uploadImage(file);
        } catch (err) {
          console.error("Upload interrompu :", err);
          setLoading(false);
          return; // pas d'enregistrement si l'image échoue
        }
      }

      if (!imageUrl) {
        setError(
          "Aucune image valide. Vérifie ton antivirus ou colle directement une URL d'image."
        );
        setLoading(false);
        return;
      }

      const payload = {
        name: form.name,
        nameLower: (form.name || "").toLowerCase(),
        region: form.region,
        year: form.year ? Number(form.year) : null,
        price: form.price ? Math.round(Number(form.price)) : null,
        cost: form.cost ? Math.round(Number(form.cost)) : null,
        barcode: form.barcode || "",
        description: form.description || "",
        imageUrl,
        category: form.category || "",
        type: form.type || "",
        forSale: !!form.forSale,
        trackStock: !!form.trackStock,
        stock: Number(form.stock) || 0,
      };

      if (editingId) {
        const ref = doc(db, "winesek", editingId);
        await updateDoc(ref, { ...payload, updatedAt: new Date() });
        setSuccess("Vin mis à jour avec succès.");
      } else {
        await addDoc(collection(db, "winesek"), {
          ...payload,
          createdAt: new Date(),
        });
        setSuccess("Vin ajouté avec succès.");
      }

      // reset form
      setForm({
        name: "",
        region: "",
        year: "",
        price: "",
        cost: "",
        barcode: "",
        description: "",
        imageUrl: "",
        category: categories[0]?.name || "",
        type: categories[0]?.types?.[0] || "",
        forSale: true,
        trackStock: true,
        stock: 0,
      });
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      setPreview("");
      setUploadProgress(0);
      setEditingId(null);

      await fetchWines();
    } catch (err) {
      console.error(err);
      setError("Erreur lors de l'enregistrement. Réessayez.");
    } finally {
      setLoading(false);
    }
  };

  // --- Éditer un vin (pré-remplir le formulaire)
  const startEdit = async (id) => {
    setLoadingWine(true);
    setError("");
    try {
      const d = await getDoc(doc(db, "winesek", id));
      if (!d.exists()) {
        setError("Vin introuvable.");
        setLoadingWine(false);
        return;
      }
      const data = d.data() || {};
      setForm({
        name: data.name || "",
        region: data.region || "",
        year: data.year ?? "",
        price: data.price ?? "",
        cost: data.cost ?? "",
        barcode: data.barcode || "",
        description: data.description || "",
        imageUrl: data.imageUrl || "",
        category: data.category || (categories[0]?.name || ""),
        type:
          data.type ||
          (categories.find((c) => c.name === data.category)?.types?.[0] ||
            ""),
        forSale: typeof data.forSale === "boolean" ? data.forSale : true,
        trackStock:
          typeof data.trackStock === "boolean" ? data.trackStock : true,
        stock: typeof data.stock === "number" ? data.stock : 0,
      });
      setPreview(data.imageUrl || "");
      setFile(null); // important : on repart sans fichier, l'utilisateur choisit s'il veut changer
      setEditingId(id);
    } catch (err) {
      console.error(err);
      setError("Impossible de charger le vin pour édition.");
    } finally {
      setLoadingWine(false);
    }
  };

  // --- Annuler l'édition
  const cancelEdit = () => {
    setEditingId(null);
    setForm({
      name: "",
      region: "",
      year: "",
      price: "",
      cost: "",
      barcode: "",
      description: "",
      imageUrl: "",
      category: categories[0]?.name || "",
      type: categories[0]?.types?.[0] || "",
      forSale: true,
      trackStock: true,
      stock: 0,
    });
    setFile(null);
    setPreview("");
    setError("");
    setSuccess("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // --- Supprimer 1 vin
  const deleteWine = async (id) => {
    try {
      await deleteDoc(doc(db, "winesek", id));
    } catch (err) {
      console.error("Erreur suppression :", err);
      throw err;
    }
  };

  // --- Gestion sélection
  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      if (allSelected) return new Set(); // unselect all
      return new Set(allIds); // select all
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const deleteSelected = async () => {
    if (selectedIds.size === 0) return;
    if (
      !window.confirm(
        `Supprimer ${selectedIds.size} vin${
          selectedIds.size > 1 ? "s" : ""
        } sélectionné${selectedIds.size > 1 ? "s" : ""} ?`
      )
    )
      return;

    setDeletingBulk(true);
    try {
      const ids = Array.from(selectedIds);
      await Promise.all(ids.map((id) => deleteWine(id)));
      clearSelection();
      await fetchWines();
      setSuccess("Suppression effectuée.");
    } catch (err) {
      console.error(err);
      setError("Une erreur est survenue pendant la suppression.");
    } finally {
      setDeletingBulk(false);
    }
  };

  const fmt = (v) =>
    new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "XOF",
    }).format(Number(v) || 0);

  // --- UI
  return (
    <div className="container-fluid px-2 px-md-4 py-4">
      <div
        className="card card-glass shadow-sm mx-auto addwine-card border-0"
        style={{ maxWidth: 1200 }}
      >
        <div className="card-body">
          {/* HEADER */}
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-start gap-2 mb-3">
            <div>
              <h3 className="card-title mb-1">
                {editingId ? "✏️ Éditer un vin" : "➕ Ajouter un vin"}
              </h3>
              <p className="text-muted mb-0">
                {editingId
                  ? "Modifiez les informations puis enregistrez."
                  : "Ajoutez une bouteille à votre inventaire."}
              </p>
            </div>
            <div className="text-md-end mt-2 mt-md-0">
              <div className="small text-muted">Inventaire global</div>
              <div className="fw-semibold">
                {loadingList ? "…" : `${winesList.length} produit(s)`}
              </div>
            </div>
          </div>

          {error && <div className="alert alert-danger py-2">{error}</div>}
          {success && (
            <div className="alert alert-success py-2">{success}</div>
          )}

          {/* FORM + PREVIEW */}
          <form onSubmit={handleSubmit} className="mt-3">
            <div className="row g-4">
              {/* Colonne gauche : formulaire */}
              <div className="col-12 col-lg-8">
                <div className="row g-3">
                  <div className="col-12 col-md-6 form-floating">
                    <input
                      ref={nameInputRef} // 👈 focus ici
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      className="form-control"
                      placeholder="Nom du vin"
                      required
                    />
                    <label>Nom du vin *</label>
                  </div>

                  <div className="col-12 col-md-6 form-floating">
                    <input
                      name="region"
                      value={form.region}
                      onChange={handleChange}
                      className="form-control"
                      placeholder="Région"
                      required
                    />
                    <label>Région *</label>
                  </div>

                  <div className="col-6 col-md-3 form-floating">
                    <input
                      name="year"
                      value={form.year || ""}
                      onChange={handleChange}
                      className="form-control"
                      placeholder="Année"
                      maxLength={4}
                    />
                    <label>Année</label>
                  </div>

                  <div className="col-6 col-md-3 form-floating">
                    <input
                      name="price"
                      value={form.price || ""}
                      onChange={handleChange}
                      type="number"
                      className="form-control"
                      placeholder="Prix (FCFA)"
                      step="1"
                      min="0"
                    />
                    <label>Prix (FCFA)</label>
                  </div>

                  <div className="col-6 col-md-3 form-floating">
                    <input
                      name="cost"
                      value={form.cost || ""}
                      onChange={handleChange}
                      type="number"
                      className="form-control"
                      placeholder="Coût (FCFA)"
                      step="1"
                      min="0"
                    />
                    <label>Coût (FCFA)</label>
                  </div>

                  <div className="col-6 col-md-3 form-floating">
                    <input
                      name="barcode"
                      value={form.barcode || ""}
                      onChange={handleChange}
                      className="form-control"
                      placeholder="Code-barres / UPC"
                    />
                    <label>Code-barres / UPC</label>
                  </div>

                  <div className="col-6 col-md-4">
                    <label className="form-label">Catégorie</label>
                    <select
                      name="category"
                      value={form.category}
                      onChange={handleCategoryChange}
                      className="form-select"
                    >
                      {loadingCats ? (
                        <option>Chargement...</option>
                      ) : (
                        categories.map((c) => (
                          <option key={c.id} value={c.name}>
                            {c.name}
                          </option>
                        ))
                      )}
                    </select>
                  </div>

                  <div className="col-6 col-md-4">
                    <label className="form-label">Type</label>
                    <select
                      name="type"
                      value={form.type}
                      onChange={handleChange}
                      className="form-select"
                    >
                      {(categories.find((c) => c.name === form.category)
                        ?.types || []
                      ).map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-6 col-md-2">
                    <label className="form-label">Stock</label>
                    <input
                      name="stock"
                      value={form.stock}
                      onChange={handleChange}
                      type="number"
                      className="form-control"
                      placeholder="Stock"
                      min="0"
                    />
                  </div>

                  <div className="col-6 col-md-2 d-flex flex-column justify-content-end">
                    <div className="form-check mb-1">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="forSale"
                        name="forSale"
                        checked={form.forSale}
                        onChange={handleChange}
                      />
                      <label
                        className="form-check-label small"
                        htmlFor="forSale"
                      >
                        À vendre
                      </label>
                    </div>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="trackStock"
                        name="trackStock"
                        checked={form.trackStock}
                        onChange={handleChange}
                      />
                      <label
                        className="form-check-label small"
                        htmlFor="trackStock"
                      >
                        Suivre le stock
                      </label>
                    </div>
                  </div>

                  <div className="col-12">
                    <label className="form-label">
                      Image (URL ou upload) — obligatoire
                    </label>
                    <input
                      type="url"
                      name="imageUrl"
                      value={form.imageUrl}
                      onChange={handleChange}
                      className="form-control mb-2"
                      placeholder="Coller une URL d'image"
                    />
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleFile}
                      className="form-control"
                    />

                    {uploading && (
                      <div className="progress mt-2" style={{ height: 8 }}>
                        <div
                          className="progress-bar"
                          role="progressbar"
                          style={{ width: `${uploadProgress}%` }}
                          aria-valuenow={uploadProgress}
                          aria-valuemin="0"
                          aria-valuemax="100"
                        />
                      </div>
                    )}
                  </div>

                  <div className="col-12 form-floating">
                    <textarea
                      name="description"
                      value={form.description}
                      onChange={handleChange}
                      className="form-control"
                      placeholder="Description"
                      style={{ minHeight: 120 }}
                    />
                    <label>Description</label>
                  </div>

                  <div className="col-12 d-flex flex-column flex-md-row align-items-stretch align-items-md-center gap-2">
                    <div className="text-muted small">
                      Les champs marqués * sont obligatoires
                    </div>

                    <div className="ms-md-auto d-flex flex-wrap gap-3">
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => navigate(-1)}
                        disabled={loading || uploading}
                      >
                        Annuler
                      </button>
                      {editingId ? (
                        <>
                          <button
                            type="button"
                            className="btn btn-outline-danger"
                            onClick={cancelEdit}
                            disabled={loading || uploading}
                          >
                            Annuler
                          </button>
                          <button
                            className="btn btn-primary btn-lg"
                            type="submit"
                            disabled={loading || uploading}
                          >
                            {loading ? (
                              <span
                                className="spinner-border spinner-border-sm me-2"
                                role="status"
                              />
                            ) : null}
                            Enregistrer
                          </button>
                        </>
                      ) : (
                        <button
                          className="btn btn-primary btn-lg"
                          type="submit"
                          disabled={loading || uploading}
                        >
                          {loading ? (
                            <span
                              className="spinner-border spinner-border-sm me-2"
                              role="status"
                            />
                          ) : null}
                          Enregistrer
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Colonne droite : aperçu produit */}
              <div className="col-12 col-lg-4">
                <div className="card shadow-sm border-0 mb-3 mb-lg-0">
                  {preview ? (
                    <img
                      src={preview}
                      alt={form.name || "Aperçu produit"}
                      className="card-img-top"
                      style={{ height: 180, objectFit: "cover" }}
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = "/placeholder.png";
                      }}
                    />
                  ) : (
                    <div
                      className="bg-light d-flex align-items-center justify-content-center"
                      style={{ height: 180 }}
                    >
                      <span className="text-muted small">Aperçu image</span>
                    </div>
                  )}
                  <div className="card-body">
                    <div className="d-flex flex-wrap gap-2 mb-2">
                      {form.category && (
                        <span className="badge badge-cat">
                          {form.category}
                        </span>
                      )}
                      {form.type && (
                        <span className="badge badge-type">{form.type}</span>
                      )}
                      {form.forSale ? (
                        <span className="badge bg-success-subtle text-success">
                          À la vente
                        </span>
                      ) : (
                        <span className="badge bg-secondary-subtle text-muted">
                          Hors vente
                        </span>
                      )}
                    </div>
                    <h5 className="mb-1">{form.name || "Nom du vin"}</h5>
                    <p className="small text-muted mb-1">
                      {form.region || "Région"} • {form.year || "Année"}
                    </p>
                    <p className="mb-2 fw-semibold">
                      {form.price ? fmt(form.price) : "Prix non renseigné"}
                    </p>
                    <p className="small text-muted mb-1">
                      Stock :{" "}
                      <strong>
                        {Number(form.stock || 0).toLocaleString()}
                      </strong>{" "}
                      {form.trackStock ? "(suivi)" : "(non suivi)"}
                    </p>
                    {form.barcode && (
                      <p className="small text-muted mb-0">
                        Code-barres : <code>{form.barcode}</code>
                      </p>
                    )}
                  </div>
                </div>

                <div className="mt-3 small text-muted">
                  <div>Aperçu catégorie :</div>
                  <img
                    src={
                      categories.find((c) => c.name === form.category)
                        ?.imageUrl || "/placeholder.png"
                    }
                    alt="cat"
                    style={{
                      width: 90,
                      height: 90,
                      objectFit: "cover",
                      borderRadius: 10,
                    }}
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = "/placeholder.png";
                    }}
                  />
                </div>
              </div>
            </div>
          </form>

          <hr className="my-4" />

          {/* Barre d’actions sélection multiple */}
          <div className="d-flex flex-wrap align-items-center justify-content-between mb-3 gap-2">
            <div className="d-flex flex-wrap align-items-center gap-2">
              <button
                type="button"
                className={`btn btn-sm ${
                  selectMode ? "btn-secondary" : "btn-outline-secondary"
                }`}
                onClick={() => {
                  setSelectMode((v) => !v);
                  if (selectMode) clearSelection();
                }}
                disabled={loadingList || winesList.length === 0}
              >
                {selectMode ? "Quitter la sélection" : "Mode sélection"}
              </button>

              {selectMode && (
                <>
                  <div className="form-check ms-2">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="check-all"
                      checked={allSelected}
                      onChange={toggleSelectAll}
                    />
                    <label className="form-check-label" htmlFor="check-all">
                      Tout sélectionner
                    </label>
                  </div>

                  <span className="text-muted small">
                    {selectedCount} sélectionné
                    {selectedCount > 1 ? "s" : ""}
                  </span>
                </>
              )}
            </div>

            {selectMode && (
              <button
                type="button"
                className="btn btn-sm btn-danger"
                disabled={selectedCount === 0 || deletingBulk}
                onClick={deleteSelected}
                title="Supprimer tous les éléments sélectionnés"
              >
                {deletingBulk
                  ? "Suppression…"
                  : `Supprimer la sélection (${selectedCount})`}
              </button>
            )}
          </div>

          {/* Liste inventaire */}
          <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
            <h5 className="mb-0">Inventaire actuel</h5>
            <div className="small text-muted">
              {loadingList ? "Chargement…" : `${winesList.length} vins`}
            </div>
          </div>

          {loadingList ? (
            <p className="text-muted">Chargement…</p>
          ) : winesList.length === 0 ? (
            <p className="no-results">Aucun vin.</p>
          ) : (
            <div className="row">
              {winesList.map((w) => {
                const checked = selectedIds.has(w.id);
                const onSale =
                  typeof w.forSale === "boolean" ? w.forSale : true;
                const track =
                  typeof w.trackStock === "boolean" ? w.trackStock : true;
                const stockVal =
                  typeof w.stock === "number" ? w.stock : 0;

                return (
                  <div key={w.id} className="col-12 col-md-6 col-lg-4 mb-3">
                    <div className="card h-100 position-relative shadow-sm border-0">
                      {/* Checkbox overlay en mode sélection */}
                      {selectMode && (
                        <div
                          className="position-absolute"
                          style={{ top: 10, left: 10, zIndex: 2 }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={checked}
                            onChange={() => toggleSelect(w.id)}
                            aria-label={`Sélectionner ${w.name}`}
                          />
                        </div>
                      )}

                      {w.imageUrl ? (
                        <img
                          src={w.imageUrl}
                          alt={w.name}
                          className="card-img-top"
                          style={{ height: 160, objectFit: "cover" }}
                          onError={(e) => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = "/placeholder.png";
                          }}
                          referrerPolicy="no-referrer"
                        />
                      ) : (
                        <div
                          style={{ height: 160 }}
                          className="bg-light d-flex align-items-center justify-content-center"
                        >
                          <span className="text-muted small">Sans image</span>
                        </div>
                      )}

                      <div className="card-body d-flex flex-column">
                        <div className="d-flex flex-wrap gap-1 mb-2">
                          {w.category && (
                            <span className="badge badge-cat">
                              {w.category}
                            </span>
                          )}
                          {w.type && (
                            <span className="badge badge-type">{w.type}</span>
                          )}
                          {onSale ? (
                            <span className="badge bg-success-subtle text-success">
                              À la vente
                            </span>
                          ) : (
                            <span className="badge bg-secondary-subtle text-muted">
                              Hors vente
                            </span>
                          )}
                        </div>
                        <h6 className="card-title mb-1">{w.name}</h6>
                        <p className="small text-muted mb-1">
                          {w.region} • {w.year || "-"}
                        </p>
                        {w.barcode && (
                          <p className="small text-muted mb-1">
                            CB : <code>{w.barcode}</code>
                          </p>
                        )}
                        <p className="mb-1 fw-semibold text-primary">
                          {fmt(w.price)}
                          {w.cost ? (
                            <span className="small text-muted ms-1">
                              (coût {fmt(w.cost)})
                            </span>
                          ) : null}
                        </p>
                        <p className="small text-muted mb-2">
                          Stock :{" "}
                          <strong>
                            {Number(stockVal || 0).toLocaleString()}
                          </strong>{" "}
                          {track ? "(suivi)" : "(non suivi)"}
                        </p>

                        <div className="mt-auto d-flex justify-content-between align-items-center flex-wrap gap-2">
                          {!selectMode ? (
                            <div className="d-flex flex-wrap align-items-center gap-2">
                              <button
                                className="btn btn-sm btn-outline-secondary"
                                onClick={() => startEdit(w.id)}
                                disabled={loadingWine}
                                title="Éditer ce vin"
                              >
                                Éditer ici
                              </button>

                              <Link
                                to={`/edit/${w.id}`}
                                className="btn btn-sm btn-outline-info"
                                title="Éditer sur une page séparée"
                              >
                                Éditer page
                              </Link>

                              <button
                                className="btn btn-sm btn-outline-danger"
                                title="Supprimer ce vin"
                                onClick={async () => {
                                  if (
                                    !window.confirm(
                                      `Supprimer "${w.name}" ? Cette action est irréversible.`
                                    )
                                  )
                                    return;
                                  try {
                                    await deleteWine(w.id);
                                    await fetchWines();
                                  } catch (err) {
                                    console.error(err);
                                    alert(
                                      "Erreur lors de la suppression du vin."
                                    );
                                  }
                                }}
                              >
                                Supprimer
                              </button>
                            </div>
                          ) : (
                            <div className="text-muted small">
                              {checked ? "Sélectionné" : "—"}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
